<template>
  <b-modal
    id="modalTaskTerminate"
    ref="modalTaskTerminate"
    :no-enforce-focus="true"
    :no-close-on-backdrop="true"
    size="lg"
    @hidden="onHidden"
  >
    <div slot="modal-title">
      {{ 'Terminate task' | translate }}
      <span v-if="task && (task.name || task.code)" class="text-muted ml-2">
        — {{ task.name || task.code }}
      </span>
    </div>

    <div v-if="task && task.quotation_detail" class="mb-3">
      <strong>{{ 'Quotation' | translate }}:</strong>
      {{ task.quotation_detail.label }}
    </div>

    <b-alert :show="!!errorMessage" variant="danger" class="mb-3">
      {{ errorMessage }}
      <ul v-if="fieldErrors.length" class="mb-0 mt-1">
        <li v-for="(err, i) in fieldErrors" :key="i">
          <strong>{{ err.field }}:</strong> {{ err.message }}
        </li>
      </ul>
    </b-alert>

    <b-form-row>
      <form-thux-horizontal-input
        :validator="$v.form.name"
        :label-form="'Installment name' | translate"
        class-form="input-row-col my-2 pl-2"
        labelColsSm="0"
        labelColsLg="0"
        type="text"
        :value="$v.form.name.$model"
        @change="$set(form, 'name', $event)"
      />
    </b-form-row>
    <b-form-row>
      <form-thux-horizontal-input
        :validator="$v.form.amount"
        :label-form="'Income' | translate"
        class-form="input-row-col my-2 pl-2"
        labelColsSm="0"
        labelColsLg="0"
        type="number"
        :min="0"
        :value="$v.form.amount.$model"
        placeholder="€"
        @change="$set(form, 'amount', $event)"
      />
      <form-thux-horizontal-datetime
        :validator="$v.form.expiration_date"
        :label-form="'Expiration date' | translate"
        class-form="input-row-col my-2 pl-2"
        labelColsSm="0"
        labelColsLg="0"
        type="date"
        :value="expirationDateMoment"
        :placeholder="$t('Enter the date')"
        @change="onExpirationDateChange($event)"
      />
    </b-form-row>
    <b-form-row>
      <form-thux-horizontal-datetime
        :validator="$v.form.date_invoice_issue"
        :label-form="'Invoice issue deadline' | translate"
        class-form="input-row-col my-2 pl-2"
        labelColsSm="0"
        labelColsLg="0"
        type="date"
        :is-required="true"
        :value="dateInvoiceIssueMoment"
        :placeholder="$t('Enter the date')"
        @change="onDateInvoiceIssueChange($event)"
      />
    </b-form-row>
    <b-form-row>
      <form-thux-horizontal-autocomplete
        :validator="$v.form.vat"
        :label-form="'VAT' | translate"
        class-form="input-row-col my-2 pl-2"
        labelColsSm="0"
        labelColsLg="0"
        label-search="name"
        :is-required="true"
        :customize-option="true"
        :value="vatAutocompleteValue"
        :options="vatOptions"
        :placeholder="$t('VAT')"
        :min-length="0"
        :internal-search="false"
        @search="onSearchVat"
        @select="onSelectVat"
        @clear="onClearVat"
      />
    </b-form-row>

    <div slot="modal-footer">
      <b-btn class="mr-2" variant="default" :disabled="submitting" @click="close">
        {{ 'Cancel' | translate }}
      </b-btn>
      <b-btn
        class="mr-2"
        variant="outline-success"
        :disabled="submitting"
        @click="onTerminateOnly"
      >
        <i class="fas fa-check mr-2"></i>
        {{ 'Terminate without billing' | translate }}
      </b-btn>
      <b-btn
        variant="success"
        :disabled="$v.form.$invalid || submitting"
        @click="onSubmit"
      >
        <i class="fas fa-flag-checkered mr-2"></i>
        {{ 'Terminate and create installment' | translate }}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { required, minValue } from 'vuelidate/lib/validators'

import { axiosThux } from '@/axiosThux'
import FormThuxHorizontalInput from '@/components/form-thux-horizontal-components/FormThuxHorizontalInput'
import FormThuxHorizontalDatetimePicker from '@/components/form-thux-horizontal-components/FormThuxHorizontalDatetimePicker'
import FormHorizontalAutocompleteThux from '@/components/form-thux-horizontal-components/FormThuxHorizontalAutocomplete'
import { TYPES as TASK_TYPES } from '../store'

const DEFAULT_EXPIRATION_DAYS = 30

export default {
  name: 'TaskTerminateModal',
  components: {
    'form-thux-horizontal-input': FormThuxHorizontalInput,
    'form-thux-horizontal-datetime': FormThuxHorizontalDatetimePicker,
    'form-thux-horizontal-autocomplete': FormHorizontalAutocompleteThux
  },
  props: {
    task: { type: Object, required: true }
  },
  data () {
    return {
      form: {
        name: '',
        amount: null,
        expiration_date: null,
        date_invoice_issue: null,
        vat: null,
        vat_name: '',
        printable: true,
        is_billed: false
      },
      vatOptions: [],
      submitting: false,
      errorMessage: '',
      fieldErrors: []
    }
  },
  computed: {
    expirationDateMoment () {
      if (!this.form.expiration_date) return undefined
      return moment(new Date(this.form.expiration_date))
    },
    dateInvoiceIssueMoment () {
      if (!this.form.date_invoice_issue) return undefined
      return moment(new Date(this.form.date_invoice_issue))
    },
    vatAutocompleteValue () {
      if (!this.form.vat) return null
      return { value: this.form.vat, name: this.form.vat_name || `#${this.form.vat}` }
    }
  },
  mounted () {
    this.preFill()
    this.fetchVatDefaults()
    this.$refs.modalTaskTerminate.show()
  },
  methods: {
    ...mapActions({
      terminateAndBill: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.terminateAndBill,
      markDone: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.markDone
    }),
    preFill () {
      const t = this.task || {}
      const expiration = moment().add(DEFAULT_EXPIRATION_DAYS, 'days').format('YYYY-MM-DD')
      this.$set(this, 'form', {
        name: t.name || t.subject || t.code || '',
        amount: t.income != null ? t.income : null,
        expiration_date: expiration,
        date_invoice_issue: null,
        vat: null,
        vat_name: '',
        printable: true,
        is_billed: false
      })
    },
    fetchVatDefaults () {
      const profile = this.$store.getters['auth/profile']
      const quotationId = this.task && this.task.quotation
      if (!quotationId) return
      const url = `/api/v1/quotation/${profile}/quotation-installment/?quotation=${quotationId}&page_size=1`
      axiosThux.get(url).then((response) => {
        const first = response.data && response.data.results && response.data.results[0]
        if (first && first.vat && !this.form.vat) {
          this.$set(this.form, 'vat', first.vat)
          this.$set(this.form, 'vat_name', first.vat_name || '')
        }
      }).catch(() => {})
    },
    onSearchVat (value) {
      const profile = this.$store.getters['auth/profile']
      const params = new URLSearchParams()
      params.append('filter__status', '1')
      if (value) params.append('filter__name__icontains', value)
      const url = `/api/v1/vat/${profile}/vat/?${params.toString()}`
      axiosThux.get(url).then((response) => {
        const items = (response.data && response.data.results) || []
        this.$set(this, 'vatOptions', items.map((v) => ({ value: v.code, name: v.name })))
      }).catch(() => {
        this.$set(this, 'vatOptions', [])
      })
    },
    onSelectVat (item) {
      this.$set(this.form, 'vat', item.value)
      this.$set(this.form, 'vat_name', item.name)
    },
    onClearVat () {
      this.$set(this.form, 'vat', null)
      this.$set(this.form, 'vat_name', '')
    },
    onExpirationDateChange (evt) {
      const date = evt && evt.date
      this.$set(this.form, 'expiration_date', date && date.format ? date.format('YYYY-MM-DD') : null)
    },
    onDateInvoiceIssueChange (evt) {
      const date = evt && evt.date
      this.$set(this.form, 'date_invoice_issue', date && date.format ? date.format('YYYY-MM-DD') : null)
    },
    close () {
      this.$refs.modalTaskTerminate.hide()
    },
    onHidden () {
      this.$emit('close')
    },
    extractErrors (data) {
      this.errorMessage = ''
      this.fieldErrors = []
      if (!data) {
        this.errorMessage = this.$t('Unexpected error')
        return
      }
      if (typeof data === 'string') {
        this.errorMessage = data
        return
      }
      if (Array.isArray(data)) {
        this.errorMessage = data.join(' ')
        return
      }
      if (data.detail) {
        this.errorMessage = data.detail
      } else {
        this.errorMessage = this.$t('Validation error')
      }
      Object.keys(data).forEach((key) => {
        if (key === 'detail' || key === 'status_code') return
        const val = data[key]
        const message = Array.isArray(val) ? val.join(' ') : String(val)
        this.fieldErrors.push({ field: key, message })
      })
    },
    async onTerminateOnly () {
      if (this.submitting) return
      this.submitting = true
      this.errorMessage = ''
      this.fieldErrors = []
      try {
        const task = await this.markDone({ id: this.task.id })
        this.$emit('terminated', { taskId: this.task.id, task, installment: null })
        this.close()
      } catch (e) {
        this.extractErrors(e && e.response && e.response.data)
      } finally {
        this.submitting = false
      }
    },
    async onSubmit () {
      if (this.$v.form.$invalid || this.submitting) return
      this.submitting = true
      this.errorMessage = ''
      this.fieldErrors = []
      const payload = {
        name: this.form.name,
        amount: this.form.amount,
        expiration_date: this.form.expiration_date,
        date_invoice_issue: this.form.date_invoice_issue,
        vat: this.form.vat,
        printable: this.form.printable,
        is_billed: this.form.is_billed
      }
      try {
        const result = await this.terminateAndBill({ id: this.task.id, payload })
        this.$emit('terminated', { taskId: this.task.id, task: result.task, installment: result.installment })
        this.close()
      } catch (e) {
        this.extractErrors(e && e.response && e.response.data)
      } finally {
        this.submitting = false
      }
    }
  },
  validations: {
    form: {
      name: { required },
      amount: { required, minValue: minValue(0) },
      expiration_date: { required },
      date_invoice_issue: { required },
      vat: { required }
    }
  }
}
</script>
